import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Box, MenuItem, Paper, Typography} from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2";
import {SettingsOutlined} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import Form, {MuiRFF, Yup} from "../../../../../../components/form";
import Button from "../../../../../../components/base/button/button";
import Api from "../../../../../../../core/services/api/api";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import ValidateMessages from "../../../../../../../core/models/static/texts/vallidate-messages";
import {deepEqual} from "../../../../../../../core/services/utils/utils";
import {useDispatch} from "react-redux";
import {ReduxActions} from "../../../../../../redux";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import CopyIconButton from "../../../../../../components/base/copy-to-clipboard/copy-icon-button";
import ManageVendorStatusesDialog from "../../../../../../components/dialogs/manage-vendor-statuses";

/**
 * Editable vendor profile card for the account-info page.
 */
const VendorGeneralInfoSection = ({vendor, onUpdated, loading: loadingVendor, deleting = false, loadVendor}) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.vendor-account-info.generalInfo'});
    const [submitting, setSubmitting] = useState(false);
    const [statuses, setStatuses] = useState([]);
    const [paymentTerms, setPaymentTerms] = useState([]);
    const [loadingPaymentTerms, setLoadingPaymentTerms] = useState(false);
    const [manageStatusesOpen, setManageStatusesOpen] = useState(false);
    const isMounted = useIsMounted();
    const dispatch = useDispatch();
    const operatorAccessLevels = useAccessLevels();

    const schema = useMemo(() => Yup.object().shape({
        name: Yup.string().trim().nullable().required(ValidateMessages.required),
        email: Yup.string().email(ValidateMessages.incorrect).trim().nullable(),
        phone: Yup.string().trim().nullable(),
        paymentTermsCode: Yup.string().trim().nullable(),
        statusId: Yup.mixed().nullable(),
    }), []);

    const validate = useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    const initialValues = useMemo(() => ({
        name: vendor?.name ?? '',
        email: vendor?.email ?? '',
        phone: vendor?.phone ?? '',
        paymentTermsCode: vendor?.paymentTermsCode ?? '',
        statusId: vendor?.statusId ?? null,
    }), [vendor]);

    /**
     * Loads vendor status options for the status select.
     */
    const loadStatuses = useCallback(async () => {
        const response = await Api.listVendorStatuses();
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setStatuses(response.data ?? []);
        }
        return response?.data ?? [];
    }, [isMounted]);

    /**
     * Loads payment-term codes for the profile select (API uses code).
     */
    const loadPaymentTerms = useCallback(async () => {
        setLoadingPaymentTerms(true);
        const response = await Api.getAllPaymentTerms();
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setPaymentTerms(response.data ?? []);
        }
        setLoadingPaymentTerms(false);
    }, [isMounted]);

    /**
     * Loads statuses and payment-terms on mount.
     */
    useEffect(() => {
        void loadStatuses();
        void loadPaymentTerms();
    }, [loadPaymentTerms, loadStatuses]);

    /**
     * Options for the payment-terms select, including the vendor's current code if missing from API list.
     */
    const paymentTermsOptions = useMemo(() => {
        const list = paymentTerms ?? [];
        const currentCode = vendor?.paymentTermsCode;
        if (!currentCode || list.some((term) => term.code === currentCode)) {
            return list;
        }
        return [
            {code: currentCode, description: currentCode},
            ...list,
        ];
    }, [paymentTerms, vendor?.paymentTermsCode]);

    /**
     * Builds PascalCase updatedProperties and calls Update / ChangeStatus as needed.
     */
    const onSubmit = useCallback(async (values) => {
        if (!vendor?.id)
            return;
        setSubmitting(true);

        const name = values.name ?? '';
        const email = values.email ?? '';
        const phone = values.phone ?? '';
        const paymentTermsCode = values.paymentTermsCode ?? '';
        const statusId = values.statusId;

        const updatedProperties = [];
        if (name !== (vendor.name ?? ''))
            updatedProperties.push('Name');
        if (email !== (vendor.email ?? ''))
            updatedProperties.push('Email');
        if (phone !== (vendor.phone ?? ''))
            updatedProperties.push('Phone');
        if (paymentTermsCode !== (vendor.paymentTermsCode ?? ''))
            updatedProperties.push('PaymentTermsCode');

        const statusChanged = statusId !== vendor.statusId;

        if (!updatedProperties.length && !statusChanged) {
            setSubmitting(false);
            return;
        }

        let next = {...vendor, name, email, phone, paymentTermsCode, statusId};

        const requests = [];
        if (updatedProperties.length) {
            requests.push(Api.updateVendorV2({
                id: vendor.id,
                name,
                email,
                phone,
                paymentTermsCode,
                updatedProperties,
            }));
        }
        if (statusChanged) {
            requests.push(Api.changeVendorStatus({
                vendorId: vendor.id,
                statusId,
            }));
        }

        const responses = await Promise.all(requests);
        if (!isMounted())
            return;

        const success = responses.every((response) => response?.resultFlag);

        setSubmitting(false);
        if (success) {
            next = {
                ...next,
                statusName: statuses.find(s => s.id === statusId)?.name ?? vendor.statusName,
            };
            dispatch(ReduxActions.vendorInformation.setVendorInformation({
                id: next.id,
                name: next.name,
                code: next.code,
                email: next.email,
                phone: next.phone,
                paymentTermsCode: next.paymentTermsCode,
                statusId: next.statusId,
                statusName: next.statusName,
            }));
            onUpdated?.(next);
        }
    }, [dispatch, isMounted, onUpdated, statuses, vendor]);

    const canManageAccountInfo = operatorAccessLevels.packages.base.manageVendorAccountInfo;
    const busy = submitting || deleting || !canManageAccountInfo;

    /**
     * Applies statuses returned from the manage dialog (if close did not occur mid-load).
     */
    const onManageStatusesClose = useCallback(async (nextStatuses) => {
        setManageStatusesOpen(false);
        let statuses;
        if (Array.isArray(nextStatuses)) {
            statuses = nextStatuses
            setStatuses(nextStatuses);
        } else {
            statuses = (await loadStatuses()) ?? [];
        }
        if (statuses.length) {
            const currentStatusId = vendor?.statusId;
            if (currentStatusId && !nextStatuses.some((s) => s.id === currentStatusId)) {
                // Current status was removed, reset to first available status
                // we fetch the latest vendor info to ensure we have the latest statusId
                void loadVendor();
            }
        }
    }, [loadStatuses, loadVendor, vendor?.statusId]);

    return (
        <Paper sx={{p: 2, display: 'flex', flexDirection: 'column', height: '100%'}}>
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBlockEnd: 3,
                gap: 2,
            }}>
                <Typography variant={'subtitle1'} fontWeight={600}>
                    {t('title')}
                </Typography>
                {
                    !!vendor?.code &&
                    <Box sx={{
                        paddingInline: 2,
                        paddingBlock: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor: 'background.tertiary',
                        borderRadius: 3,
                    }}>
                        <Typography
                            variant={'body1'}
                            fontWeight={600}
                            color={'tertiary.main'}
                            sx={{display: 'inline-flex', alignItems: 'center'}}
                        >
                            <Typography
                                variant={'body2'}
                                component={'span'}
                                color={'text.secondary'}
                                fontWeight={400}
                                marginInlineEnd={0.5}
                            >
                                {t('codeLabel')}
                            </Typography>
                            {vendor.code}
                            <CopyIconButton value={vendor.code}/>
                        </Typography>
                    </Box>
                }
            </Box>
            {
                loadingVendor
                    ? <Typography variant={'body2'} color={'text.secondary'}>{t('loading')}</Typography>
                    : (
                        <Form
                            key={vendor?.id ?? 'vendor-profile'}
                            onSubmit={onSubmit}
                            validate={validate}
                            initialValues={initialValues}
                            formProps={{id: 'vendor-general-info-form'}}
                        >
                            {({values}) => (
                                <Grid2 container spacing={2}>
                                    <Grid2 xs={12} md={6}>
                                        <MuiRFF.TextField
                                            name={'name'}
                                            label={t('name')}
                                            required={required.name}
                                            fullWidth
                                            disabled={busy}
                                        />
                                    </Grid2>
                                    <Grid2 xs={12} md={6}>
                                        <MuiRFF.TextField
                                            name={'email'}
                                            label={t('email')}
                                            fullWidth
                                            disabled={busy}
                                        />
                                    </Grid2>
                                    <Grid2 xs={12} md={6}>
                                        <MuiRFF.TextField
                                            name={'phone'}
                                            label={t('phone')}
                                            fullWidth
                                            disabled={busy}
                                        />
                                    </Grid2>
                                    <Grid2 xs={12} md={6}>
                                        <MuiRFF.TextField
                                            name={'paymentTermsCode'}
                                            label={t('paymentTerms')}
                                            select
                                            fullWidth
                                            disabled={busy || loadingPaymentTerms}
                                            SelectProps={{
                                                renderValue: (code) => {
                                                    if (!code) return '';
                                                    const match = paymentTermsOptions.find((term) => term.code === code);
                                                    return match ? `${match.description} (${match.code})` : code;
                                                },
                                            }}
                                        >
                                            {paymentTermsOptions.map((term) => (
                                                <MenuItem key={term.code ?? term.id} value={term.code}>
                                                    {term.description} ({term.code})
                                                </MenuItem>
                                            ))}
                                        </MuiRFF.TextField>
                                    </Grid2>
                                    <Grid2 xs={12} md={6}>
                                        <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%'}}>
                                            <MuiRFF.TextField
                                                name={'statusId'}
                                                label={t('status')}
                                                select
                                                fullWidth
                                                disabled={busy}
                                                sx={{flex: 1, minWidth: 0}}
                                            >
                                                {statuses.map((status) => (
                                                    <MenuItem key={status.id} value={status.id}>
                                                        {status.name}
                                                    </MenuItem>
                                                ))}
                                                {
                                                    canManageAccountInfo &&
                                                    <MenuItem
                                                        value={undefined}
                                                        sx={{
                                                            display: 'flex', alignItems: 'center', gap: 1,
                                                            backgroundColor: 'primary.main', color: 'primary.contrastText',
                                                        }}
                                                        onClickCapture={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            setManageStatusesOpen(true);
                                                        }}
                                                    >
                                                        <SettingsOutlined fontSize={'small'}/>
                                                        {t('manageStatuses')}
                                                    </MenuItem>
                                                }
                                            </MuiRFF.TextField>
                                        </Box>
                                    </Grid2>
                                    {
                                        canManageAccountInfo &&
                                        <Grid2 xs={12} md={6} className={'d-flex justify-content-center'}>
                                            <Button
                                                type={'submit'}
                                                form={'vendor-general-info-form'}
                                                variant={'contained'}
                                                color={'primary'}
                                                size={'large'}
                                                className={'w-100'}
                                                disabled={busy || deepEqual(initialValues, values)}
                                            >
                                                {submitting ? t('updating') : t('update')}
                                            </Button>
                                        </Grid2>
                                    }
                                </Grid2>
                            )}
                        </Form>
                    )
            }
            <ManageVendorStatusesDialog
                open={manageStatusesOpen}
                onClose={onManageStatusesClose}
            />
        </Paper>
    );
};

export default VendorGeneralInfoSection;
